// PodcastPlayer.jsx — app-level podcast playback.
//
// The <audio> element + sticky PlayerBar live at the App shell so playback
// survives tab navigation inside Pronto (Daily ↔ Listen ↔ Pantry ↔ ...).
// Browser-tab switches were already fine — only same-app navigation was
// killing playback because the player used to live inside Listen and
// unmounted with it.
//
// The provider holds three pieces of state:
//   - nowPlaying: { episode, feed } | null
//   - queue:      [{episode, feed}, ...]   — snapshot taken at "Play briefing"
//   - queueIndex: number | null            — current position in the queue
//
// Listen calls playEpisode(...) for single-episode plays and playQueue(...)
// for the morning briefing. The PlayerBar autoadvances on `ended` and exposes
// AirPlay/Cast via the system picker.

import { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Pause, SkipBack, SkipForward, Cast, Speaker } from "lucide-react";
import { sonosProxyEnabled, listGroups, listGroupsSafe, playOnGroup, pauseGroup, resumeGroup, stopGroup, setGroupVolume, getGroupStatus, seekGroup } from "../lib/sonos";
import { getHistory, getResumePosition, recordLocalProgress, syncToServer } from "../lib/podcast-history";

const PodcastPlayerContext = createContext(null);

export function usePodcastPlayer() {
  const ctx = useContext(PodcastPlayerContext);
  if (!ctx) throw new Error("usePodcastPlayer must be used inside <PodcastPlayerProvider>");
  return ctx;
}

export function PodcastPlayerProvider({ children }) {
  const [nowPlaying, setNowPlaying] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(null);

  const playEpisode = useCallback((episode, feed) => {
    setNowPlaying({ episode, feed });
    setQueue([]);
    setQueueIndex(null);
  }, []);

  const playQueue = useCallback((items) => {
    if (!items?.length) return;
    setQueue(items);
    setQueueIndex(0);
    setNowPlaying(items[0]);
  }, []);

  const advanceQueue = useCallback(() => {
    if (queueIndex === null) return false;
    const next = queueIndex + 1;
    if (next < queue.length) {
      setQueueIndex(next);
      setNowPlaying(queue[next]);
      return true;
    }
    setQueueIndex(null);
    return false;
  }, [queue, queueIndex]);

  const closePlayer = useCallback(() => {
    setNowPlaying(null);
    setQueue([]);
    setQueueIndex(null);
  }, []);

  // Auto-refresh hook calls this when a newer episode arrives for a feed
  // that's already playing or sitting in the queue. We swap the queue entry
  // (so the briefing keeps using the latest) and, if the user is currently
  // on the stale episode, swap nowPlaying too — which restarts playback at
  // 0:00 of the fresh one. The history table still holds the partial
  // progress on the old episode, so it stays in "Continue listening" if
  // the user wants to come back.
  const replaceInQueueByFeed = useCallback((feedId, newPair) => {
    setQueue(curr => curr.map(item => item.feed.id === feedId ? newPair : item));
    setNowPlaying(curr => {
      if (!curr || curr.feed.id !== feedId) return curr;
      if (curr.episode.id === newPair.episode.id) return curr;
      return newPair;
    });
  }, []);

  const [sonosGroups, setSonosGroups] = useState([]);
  const [sonosTarget, setSonosTarget] = useState(null);
  // Sonos output lives here, not in the player bar, because Listen's "Play on"
  // card and the bar's speaker picker must agree on where audio is going. Two
  // copies of this state meant the card could claim "Kitchen" while the bar was
  // still playing on the phone.

  useEffect(() => {
    if (!sonosProxyEnabled()) { setSonosGroups([]); return; }
    let alive = true;
    listGroupsSafe().then(({ groups, error }) => {
      if (!alive) return;
      setSonosGroups(groups);
      // Not surfaced in the player bar (it would be noise mid-playback), but
      // logged so a failure is discoverable instead of silent.
      if (error) console.warn("[sonos] group list failed:", error);
    });
    return () => { alive = false; };
  }, []);

  const value = useMemo(() => ({
    nowPlaying, queue, queueIndex,
    queueActive: queueIndex !== null,
    queueRemaining: queueIndex !== null ? Math.max(0, queue.length - queueIndex - 1) : 0,
    playEpisode, playQueue, advanceQueue, replaceInQueueByFeed, closePlayer,
    sonosGroups, setSonosGroups, sonosTarget, setSonosTarget,
  }), [nowPlaying, queue, queueIndex, playEpisode, playQueue, advanceQueue, replaceInQueueByFeed, closePlayer,
       sonosGroups, sonosTarget]);

  return (
    <PodcastPlayerContext.Provider value={value}>
      {children}
    </PodcastPlayerContext.Provider>
  );
}

export function PodcastPlayerBar({ store }) {
  const {
    nowPlaying, queueActive, queueRemaining, advanceQueue, closePlayer,
    sonosGroups, setSonosGroups, sonosTarget, setSonosTarget,
  } = usePodcastPlayer();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [airplaySupport, setAirplaySupport] = useState("none");
  const [wirelessActive, setWirelessActive] = useState(false);
  const [rate, setRate] = useState(1);
  const [volume, setVolume] = useState(1);
  // Sonos output: null = this device; a group id = play on that Sonos group.
  const [sonosPickerOpen, setSonosPickerOpen] = useState(false);
  const [sonosBusy, setSonosBusy] = useState(false);
  const [sonosPos, setSonosPos] = useState(0); // seconds, from status poll
  const [sonosDur, setSonosDur] = useState(0); // 0 when Sonos reports no duration (streams)
  const [sonosMsg, setSonosMsg] = useState(null); // transient error toast (replaces alert())

  // Refs we need inside imperative handlers (pause/end/visibilitychange).
  // React state would close over stale values during long-lived listeners.
  const nowPlayingRef = useRef(null);
  const storeRef = useRef(store);
  const lastRecordedRef = useRef(0); // last timestamp we wrote to localStorage
  useEffect(() => { nowPlayingRef.current = nowPlaying; }, [nowPlaying]);
  useEffect(() => { storeRef.current = store; }, [store]);

  // When the episode changes, reset the audio source. If we have a saved
  // resume position from history, seek there once metadata loads. Save the
  // outgoing episode's position before swapping (handled in the cleanup
  // below — `prev` is captured by the effect closure on the previous run).
  useEffect(() => {
    if (!nowPlaying) return;
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = nowPlaying.episode.audioUrl;
    audio.load();
    const tryPlay = audio.play();
    if (tryPlay && typeof tryPlay.catch === "function") tryPlay.catch(() => {});
    setPlaying(true);

    const history = getHistory(storeRef.current?.preferences);
    const resume = getResumePosition(history, nowPlaying.episode.id);
    if (resume != null) {
      // seek once metadata is available — wiring on the audio element so
      // we can attach without waiting on React's onLoadedMetadata path.
      const onMeta = () => {
        try { audio.currentTime = resume; } catch {}
        audio.removeEventListener("loadedmetadata", onMeta);
      };
      audio.addEventListener("loadedmetadata", onMeta);
    }

    // Cleanup: snapshot the episode that was just playing so we save where
    // they left off when switching to a new one.
    const cleanupEpisode = nowPlaying;
    return () => {
      const a = audioRef.current;
      if (!a || !cleanupEpisode) return;
      const pos = a.currentTime;
      const dur = a.duration || 0;
      if (pos > 1) {
        recordLocalProgress({
          episode: cleanupEpisode.episode,
          feed: cleanupEpisode.feed,
          position_s: pos,
          duration_s: dur,
        });
        syncToServer(storeRef.current);
      }
    };
  }, [nowPlaying?.episode?.id, nowPlaying?.episode?.audioUrl]);

  // Sync local history to Supabase when the user leaves the page or
  // backgrounds the tab — catches mobile lock-screen pauses too.
  useEffect(() => {
    const flush = () => {
      const np = nowPlayingRef.current;
      const a = audioRef.current;
      if (np && a && a.currentTime > 1) {
        recordLocalProgress({
          episode: np.episode,
          feed: np.feed,
          position_s: a.currentTime,
          duration_s: a.duration || 0,
        });
      }
      syncToServer(storeRef.current);
    };
    const onVis = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Detect AirPlay / Remote Playback support, and track whether audio is
  // *currently* routing to a wireless target so the button can reflect it
  // ("Playing on AirPlay" vs. just "AirPlay…"). This is the source of truth for
  // active routing — the platform gives us this state but no device list and no
  // programmatic target selection (picker is user-gesture only).
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (typeof audio.webkitShowPlaybackTargetPicker === "function") {
      setAirplaySupport("webkit");
      // Safari fires this whenever the active route flips to/from an AirPlay
      // target; webkitCurrentPlaybackTargetIsWireless is the live truth.
      const onWireless = () => setWirelessActive(!!audio.webkitCurrentPlaybackTargetIsWireless);
      audio.addEventListener("webkitcurrentplaybacktargetiswirelesschanged", onWireless);
      onWireless();
      return () => audio.removeEventListener("webkitcurrentplaybacktargetiswirelesschanged", onWireless);
    }
    if (audio.remote && typeof audio.remote.prompt === "function") {
      setAirplaySupport("remote");
      // Remote Playback API (Chrome): connect/disconnect reflect routing.
      const sync = () => setWirelessActive(audio.remote.state === "connected");
      audio.remote.addEventListener?.("connect", sync);
      audio.remote.addEventListener?.("disconnect", sync);
      sync();
      return () => {
        audio.remote.removeEventListener?.("connect", sync);
        audio.remote.removeEventListener?.("disconnect", sync);
      };
    }
    setAirplaySupport("none");
    setWirelessActive(false);
  }, [nowPlaying?.episode?.id]);

  // Publish player height as a CSS variable so floating UI (chat bubble)
  // can lift itself above the bar.
  useEffect(() => {
    if (!nowPlaying) {
      document.documentElement.style.setProperty("--player-h", "0px");
      return;
    }
    document.documentElement.style.setProperty("--player-h", "76px");
    return () => document.documentElement.style.setProperty("--player-h", "0px");
  }, [nowPlaying]);

  // Media Session — surfaces metadata to OS-level controls and routes hardware
  // play/pause/next keys back to us.
  useEffect(() => {
    if (!nowPlaying || typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: nowPlaying.episode.title,
      artist: nowPlaying.feed.title || "",
      artwork: nowPlaying.episode.image || nowPlaying.feed.image
        ? [{ src: nowPlaying.episode.image || nowPlaying.feed.image, sizes: "512x512", type: "image/jpeg" }]
        : [],
    });
    navigator.mediaSession.setActionHandler("play", () => audioRef.current?.play());
    navigator.mediaSession.setActionHandler("pause", () => audioRef.current?.pause());
    navigator.mediaSession.setActionHandler("seekbackward", () => { if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 30); });
    navigator.mediaSession.setActionHandler("seekforward", () => { if (audioRef.current) audioRef.current.currentTime = Math.min(audioRef.current.duration || 0, audioRef.current.currentTime + 30); });
    // seekto powers OS-level scrubbing on the lock screen / Control Center.
    try {
      navigator.mediaSession.setActionHandler("seekto", (details) => {
        if (audioRef.current && Number.isFinite(details?.seekTime)) {
          audioRef.current.currentTime = details.seekTime;
        }
      });
    } catch {}
  }, [nowPlaying]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (queueActive && queueRemaining > 0) {
      navigator.mediaSession.setActionHandler("nexttrack", () => advanceQueue());
    } else {
      try { navigator.mediaSession.setActionHandler("nexttrack", null); } catch {}
    }
  }, [queueActive, queueRemaining, advanceQueue]);

  // Keep the OS lock-screen play/pause indicator in sync with our state, so
  // backgrounded playback shows the right control on the lock screen.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    try { navigator.mediaSession.playbackState = playing ? "playing" : "paused"; } catch {}
  }, [playing]);

  // Apply playback rate + volume to the element (on change and on episode swap).
  useEffect(() => {
    const a = audioRef.current;
    if (a) { a.playbackRate = rate; a.volume = volume; }
  }, [rate, volume, nowPlaying?.episode?.id]);

  // Load Sonos groups when a track is active and the proxy is enabled.
  useEffect(() => {
    if (!nowPlaying || !sonosProxyEnabled()) return;
    let alive = true;
    listGroupsSafe().then(({ groups, error }) => {
      if (!alive) return;
      setSonosGroups(groups);
      if (error) console.warn("[sonos] group list failed:", error);
    });
    return () => { alive = false; };
  }, [nowPlaying?.episode?.id]);

  // While a Sonos group is the active output, poll its state so the UI's
  // play/pause reflects Sonos (which plays independently of the local <audio>).
  useEffect(() => {
    if (!sonosTarget) return;
    let alive = true;
    const tick = () => getGroupStatus(sonosTarget)
      .then(s => {
        if (!alive) return;
        setPlaying(s?.playbackState === "PLAYBACK_STATE_PLAYING");
        if (typeof s?.positionMillis === "number") setSonosPos(s.positionMillis / 1000);
        setSonosDur(typeof s?.durationMillis === "number" && s.durationMillis > 0 ? s.durationMillis / 1000 : 0);
      })
      .catch(() => {});
    tick();
    const iv = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, [sonosTarget]);

  const SPEEDS = [1, 1.25, 1.5, 1.75, 2];
  const cycleRate = () => setRate(r => SPEEDS[(SPEEDS.indexOf(r) + 1) % SPEEDS.length]);
  const fmtRate = (r) => (Number.isInteger(r) ? `${r}×` : `${r}×`);

  // Surface Sonos errors as a transient in-bar toast instead of a blocking
  // alert() dialog.
  const notifySonos = (msg) => setSonosMsg(String(msg || "").slice(0, 200));
  useEffect(() => {
    if (!sonosMsg) return;
    const t = setTimeout(() => setSonosMsg(null), 6000);
    return () => clearTimeout(t);
  }, [sonosMsg]);

  // Switch output between this device and a Sonos group. Picking a group hands
  // the episode URL to Sonos (which plays it directly) and pauses local audio;
  // picking null stops Sonos and returns control to the device.
  const selectSonosOutput = async (groupId) => {
    setSonosPickerOpen(false);
    const prev = sonosTarget;
    if (groupId === prev) return;
    setSonosBusy(true);
    try {
      if (groupId) {
        audioRef.current?.pause();
        await playOnGroup(groupId, nowPlaying.episode.audioUrl, nowPlaying.episode.title);
        setSonosTarget(groupId);
        setPlaying(true);
      } else {
        if (prev) await stopGroup(prev).catch(() => {});
        setSonosTarget(null);
        setPlaying(false);
      }
    } catch (e) {
      notifySonos(`Sonos: ${e.message || e}`);
    } finally {
      setSonosBusy(false);
    }
  };

  const onVolumeChange = (v) => {
    setVolume(v);
    if (sonosTarget) setGroupVolume(sonosTarget, Math.round(v * 100)).catch(() => {});
  };

  const togglePlay = () => {
    // When a Sonos group is the output, the player is a remote control.
    if (sonosTarget) {
      (playing ? pauseGroup(sonosTarget) : resumeGroup(sonosTarget))
        .then(() => setPlaying(p => !p))
        .catch(e => notifySonos(`Sonos: ${e.message || e}`));
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play();
    else audio.pause();
  };

  const seek = (delta) => {
    if (sonosTarget) {
      const target = Math.max(0, sonosPos + delta);
      setSonosPos(target); // optimistic; the poll will reconcile
      seekGroup(sonosTarget, Math.round(target * 1000)).catch(e => notifySonos(`Sonos: ${e.message || e}`));
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + delta));
  };

  const onTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime);
    setDuration(audio.duration || 0);

    // Feed the OS lock-screen scrubber so backgrounded progress is accurate.
    if (typeof navigator !== "undefined" && "mediaSession" in navigator && Number.isFinite(audio.duration) && audio.duration > 0) {
      try {
        navigator.mediaSession.setPositionState({
          duration: audio.duration,
          position: Math.min(audio.currentTime, audio.duration),
          playbackRate: audio.playbackRate || 1,
        });
      } catch {}
    }

    // Cheap-write throttle: localStorage every ~10s of playback.
    const np = nowPlayingRef.current;
    if (!np) return;
    const now = Date.now();
    if (now - lastRecordedRef.current >= 10_000) {
      lastRecordedRef.current = now;
      recordLocalProgress({
        episode: np.episode,
        feed: np.feed,
        position_s: audio.currentTime,
        duration_s: audio.duration || 0,
      });
    }
  };

  const onAudioPause = () => {
    setPlaying(false);
    const audio = audioRef.current;
    const np = nowPlayingRef.current;
    if (audio && np && audio.currentTime > 1) {
      recordLocalProgress({
        episode: np.episode,
        feed: np.feed,
        position_s: audio.currentTime,
        duration_s: audio.duration || 0,
      });
      syncToServer(storeRef.current);
    }
  };

  const onAudioEnded = () => {
    setPlaying(false);
    const audio = audioRef.current;
    const np = nowPlayingRef.current;
    if (np) {
      recordLocalProgress({
        episode: np.episode,
        feed: np.feed,
        position_s: audio?.duration || 0,
        duration_s: audio?.duration || 0,
        completed: true,
      });
      syncToServer(storeRef.current);
    }
    if (queueActive) advanceQueue();
  };

  const showAirPlayPicker = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (airplaySupport === "webkit") {
      audio.webkitShowPlaybackTargetPicker();
    } else if (airplaySupport === "remote") {
      audio.remote.prompt().catch(() => {});
    } else {
      alert(
        "This browser doesn't expose an in-app AirPlay/Cast picker. " +
        "Send audio to your speakers via your system controls:\n\n" +
        "• macOS: click the Sound icon in the menu bar → choose your AirPlay device\n" +
        "• iOS: open Control Center → tap the AirPlay button on the audio card\n" +
        "• Sonos with AirPlay 2: it will appear in the system AirPlay list"
      );
    }
  };

  // Always keep the audio element mounted so it survives tab nav even when
  // the bar UI hides — but the visible UI only shows when nowPlaying is set.
  // On Sonos, progress/time come from the status poll (sonosPos/sonosDur);
  // locally they come from the <audio> element. Streams may report no duration.
  const dispCur = sonosTarget ? sonosPos : currentTime;
  const dispDur = sonosTarget ? sonosDur : duration;
  const pct = dispDur > 0 ? (dispCur / dispDur) * 100 : 0;

  return (
    <>
      <AnimatePresence>
        {nowPlaying && (
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-pronto-ink text-white shadow-2xl"
          >
            {sonosMsg && (
              <div className="px-4 py-2 bg-red-500/90 text-white text-[12px] flex items-center justify-between gap-2">
                <span className="truncate">{sonosMsg}</span>
                <button onClick={() => setSonosMsg(null)} className="opacity-80 hover:opacity-100 flex-shrink-0" aria-label="Dismiss">✕</button>
              </div>
            )}
            <div className="h-1 bg-white/10 cursor-pointer" onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const ratio = (e.clientX - rect.left) / rect.width;
              if (sonosTarget) {
                if (sonosDur > 0) { const t = sonosDur * ratio; setSonosPos(t); seekGroup(sonosTarget, Math.round(t * 1000)).catch(() => {}); }
              } else if (audioRef.current && duration > 0) {
                audioRef.current.currentTime = duration * ratio;
              }
            }}>
              <div className="h-full bg-pronto-navy-light" style={{ width: `${pct}%` }} />
            </div>

            <div className="flex items-center gap-3 px-4 py-3">
              {(nowPlaying.episode.image || nowPlaying.feed.image) && (
                <img
                  src={nowPlaying.episode.image || nowPlaying.feed.image}
                  alt=""
                  className="w-12 h-12 rounded object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] truncate font-medium">{nowPlaying.episode.title}</div>
                <div className="text-[11px] text-white/60 font-mono tabular truncate">
                  {nowPlaying.feed.title || ""}
                  {dispDur > 0 && ` · ${formatTime(dispCur)} / ${formatTime(dispDur)}`}
                </div>
              </div>
              <button onClick={() => seek(-30)} className="p-2 hover:text-white" title="Back 30s">
                <SkipBack size={16} />
              </button>
              <button onClick={togglePlay} className="w-10 h-10 grid place-items-center rounded-full bg-pronto-navy-light text-white hover:bg-pronto-navy">
                {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
              </button>
              <button onClick={() => seek(30)} className="p-2 hover:text-white" title="Forward 30s">
                <SkipForward size={16} />
              </button>
              {queueActive && queueRemaining > 0 && (
                <button
                  onClick={advanceQueue}
                  className="px-2.5 h-9 rounded-full bg-pronto-navy-light hover:bg-pronto-navy text-white text-[11px] font-mono uppercase tracking-wider flex items-center gap-1.5"
                  title="Skip to next briefing item"
                >
                  Next <span className="text-white/50">({queueRemaining})</span>
                </button>
              )}
              {/* Playback speed — tap to cycle 1× → 2× */}
              <button onClick={cycleRate} className="px-1.5 py-1 rounded-md text-[11px] font-semibold text-white/70 hover:text-white tabular min-w-[32px]" title="Playback speed">
                {fmtRate(rate)}
              </button>
              {/* Volume (wider screens) */}
              <input
                type="range" min="0" max="1" step="0.05" value={volume}
                onChange={(e) => onVolumeChange(+e.target.value)}
                className="hidden sm:block w-16 accent-pronto-navy-light cursor-pointer"
                aria-label="Volume" title="Volume"
              />
              {/* Sonos output — becomes a remote control when a group is picked */}
              {sonosProxyEnabled() && sonosGroups.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setSonosPickerOpen(o => !o)}
                    disabled={sonosBusy}
                    className={`p-2 transition-colors disabled:opacity-50 ${sonosTarget ? "text-emerald-400 hover:text-emerald-300" : "text-white/60 hover:text-white"}`}
                    title={sonosTarget ? `Playing on ${sonosGroups.find(g => g.id === sonosTarget)?.name || "Sonos"} — tap to change` : "Play on Sonos"}
                    aria-label="Sonos output"
                  >
                    <Speaker size={16} />
                  </button>
                  {sonosPickerOpen && (
                    <div className="absolute bottom-full right-0 mb-2 min-w-[180px] rounded-xl bg-pronto-navy shadow-xl border border-white/10 py-1 z-50">
                      <button
                        onClick={() => selectSonosOutput(null)}
                        className={`w-full text-left px-3 py-2 text-[13px] hover:bg-white/10 ${!sonosTarget ? "text-emerald-400" : "text-white/80"}`}
                      >
                        This device
                      </button>
                      {sonosGroups.map(g => (
                        <button
                          key={g.id}
                          onClick={() => selectSonosOutput(g.id)}
                          className={`w-full text-left px-3 py-2 text-[13px] hover:bg-white/10 ${sonosTarget === g.id ? "text-emerald-400" : "text-white/80"}`}
                        >
                          {g.name}{g.playerCount > 1 && <span className="text-white/40"> ({g.playerCount})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={showAirPlayPicker}
                className={`p-2 transition-colors ${
                  wirelessActive ? "text-emerald-400 hover:text-emerald-300"
                  : airplaySupport === "none" ? "text-white/60 hover:text-white"
                  : "text-pronto-navy-light hover:text-white"
                }`}
                title={
                  wirelessActive ? "Playing on AirPlay — tap to change output" :
                  airplaySupport === "webkit" ? "AirPlay…" :
                  airplaySupport === "remote" ? "Send to remote device…" :
                  "AirPlay / Cast (use system sound output)"
                }
                aria-label={wirelessActive ? "Playing on AirPlay — tap to change output" : "AirPlay or Cast"}
              >
                <Cast size={16} />
              </button>
              <button onClick={closePlayer} className="p-2 text-white/60 hover:text-white" title="Close player">
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Audio element is always mounted so the source/state survives the
          AnimatePresence exit transition and any cross-tab navigation. */}
      <audio
        ref={audioRef}
        preload="metadata"
        x-webkit-airplay="allow"
        hidden={!nowPlaying}
        onPlay={() => setPlaying(true)}
        onPause={onAudioPause}
        onEnded={onAudioEnded}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onTimeUpdate}
      />
    </>
  );
}

function formatTime(s) {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}
